import { FC, useState, useRef } from "react";
import { useDeleteChatMutation, useUpdateChatTitleMutation } from "@/services/chat";
import { Typography } from "@/components";
import { Tooltip, IconButton } from "@mui/material";
import { FiEdit2, FiCheck, FiX } from "react-icons/fi";
import useSnackbar from "@/hooks/useSnackbar";
import { tokens } from "@/tokens";

interface ChatListItemProps {
  id: string;
  title: string;
  isSelected: boolean;
  onClick: (chatId: string) => void;
  isFirst?: boolean;
  isLast?: boolean;
}

const ChatListItem: FC<ChatListItemProps> = ({
  id,
  title,
  isSelected,
  onClick,
  isFirst = false,
  isLast = false,
}) => {
  const { show: showSnackbar } = useSnackbar();
  const [deleteChat] = useDeleteChatMutation();
  const [updateChatTitle] = useUpdateChatTitleMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [tempTitle, setTempTitle] = useState<string>(title || "");
  const [saving, setSaving] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isSavingRef = useRef<boolean>(false);
  const commitInFlightRef = useRef<Promise<void> | null>(null);
  const handleClick = () => {
    onClick(id);
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deleteChat(id).unwrap();
    } catch (err) {
      // noop
    }
  };

  const borderRadius = (() => {
    if (isFirst && isLast) return "8px";
    if (isFirst) return "8px 8px 0 0";
    if (isLast) return "0 0 8px 8px";
    return "0";
  })();

  const backgroundColor = isSelected ? "#F7F8F8" : "transparent";

  return (
    <div
      onClick={handleClick}
      style={{
        display: "flex",
        width: "100%",
        alignItems: "center",
        cursor: "pointer",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
          padding: "8px",
          borderRadius: borderRadius,
          backgroundColor: backgroundColor,
          transition: "background-color 0.16s ease",
        }}
        onMouseEnter={(e) => {
          setIsHovered(true);
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = "#F7F8F8";
          }
        }}
        onMouseLeave={(e) => {
          setIsHovered(false);
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = "transparent";
          }
        }}
      >
      <div style={{ flex: 1, minWidth: 0 }}>
      <Tooltip
        placement="right"
        arrow
        title={title || "Untitled chat"}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: { offset: [0, -8] },
              },
            ],
          },
          tooltip: {
            sx: {
              bgcolor: '#1e293b',
              color: 'white',
              fontSize: '0.875rem',
              borderRadius: tokens.radius.md,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            },
          },
        }}
      >
        <div style={{ width: "100%" }}>
          {isEditing ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={(e) => e.stopPropagation()}>
              <input
                ref={inputRef}
                type="text"
                value={tempTitle}
                onChange={(e) => setTempTitle(e.target.value)}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (!isSavingRef.current) {
                      await (commitInFlightRef.current = (async () => {
                        try {
                          isSavingRef.current = true;
                          setSaving(true);
                          const newTitle = (tempTitle || '').trim() || 'Untitled chat';
                          if (newTitle !== (title || '')) {
                            await updateChatTitle({ id, title: newTitle }).unwrap();
                          }
                          setIsEditing(false);
                        } catch (err) {
                          try { showSnackbar('Rename failed', 'Could not update chat title. Please try again.', 'error', 4000); } catch {}
                          setIsEditing(false);
                          setTempTitle(title || '');
                        } finally {
                          setSaving(false);
                          isSavingRef.current = false;
                          commitInFlightRef.current = null;
                        }
                      })());
                    }
                  } else if (e.key === 'Escape') {
                    setIsEditing(false);
                    setTempTitle(title || '');
                  }
                }}
                onBlur={async () => {
                  if (isSavingRef.current) return;
                  await (commitInFlightRef.current = (async () => {
                    try {
                      isSavingRef.current = true;
                      setSaving(true);
                      const newTitle = (tempTitle || '').trim() || 'Untitled chat';
                      if (newTitle !== (title || '')) {
                        await updateChatTitle({ id, title: newTitle }).unwrap();
                      }
                      setIsEditing(false);
                    } catch (err) {
                      try { showSnackbar('Rename failed', 'Could not update chat title. Please try again.', 'error', 4000); } catch {}
                      setIsEditing(false);
                      setTempTitle(title || '');
                    } finally {
                      setSaving(false);
                      isSavingRef.current = false;
                      commitInFlightRef.current = null;
                    }
                  })());
                }}
                placeholder="Untitled chat"
                style={{
                  fontFamily: 'Inter Display, sans-serif',
                  fontSize: tokens.fontSize.base,
                  fontWeight: tokens.fontWeight.semibold,
                  color: isSelected ? tokens.brand.primary : '#33404D',
                  border: '1px solid #d9d9d9',
                  borderRadius: tokens.radius.sm,
                  padding: '6px 8px',
                  width: '100%',
                }}
                autoFocus
              />
              <IconButton size="small" disabled={saving} onMouseDown={(e) => e.preventDefault()} onClick={async (e) => {
                e.stopPropagation();
                if (isSavingRef.current) return;
                await (commitInFlightRef.current = (async () => {
                  try {
                    isSavingRef.current = true;
                    setSaving(true);
                    const newTitle = (tempTitle || '').trim() || 'Untitled chat';
                    if (newTitle !== (title || '')) {
                      await updateChatTitle({ id, title: newTitle }).unwrap();
                    }
                    setIsEditing(false);
                  } catch (err) {
                    try { showSnackbar('Rename failed', 'Could not update chat title. Please try again.', 'error', 4000); } catch {}
                    setIsEditing(false);
                    setTempTitle(title || '');
                  } finally {
                    setSaving(false);
                    isSavingRef.current = false;
                    commitInFlightRef.current = null;
                  }
                })());
              }} aria-label="Save title">
                <FiCheck />
              </IconButton>
              <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); if (isSavingRef.current) return; setIsEditing(false); setTempTitle(title || ''); }} aria-label="Cancel rename">
                <FiX />
              </IconButton>
            </div>
          ) : (
            <Typography
              display="block"
              style={{
                  fontFamily: 'Inter Display, sans-serif',
                  fontSize: tokens.fontSize.base,
                  fontWeight: tokens.fontWeight.semibold,
                  color: "#33404D",
                  lineHeight: "16px",
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
              }}
            >
              {title || "Untitled chat"}
            </Typography>
          )}
        </div>
      </Tooltip>
      </div>
      {(isHovered || isEditing) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {!isEditing && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
                setTempTitle(title || "");
                setTimeout(() => inputRef.current?.focus(), 0);
              }}
              aria-label="Edit title"
              title="Rename chat"
              style={{ color: tokens.text.secondary }}
            >
              <FiEdit2 />
            </IconButton>
          )}
          {!isEditing && (
            <button
              onClick={handleDelete}
              style={{
                border: "none",
                background: "transparent",
                color: "#A33",
                cursor: "pointer",
                padding: 4,
                borderRadius: tokens.radius.sm,
              }}
              aria-label="Delete chat"
              title="Delete chat"
            >
              ✕
            </button>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default ChatListItem;
